<template>
  <div>
    <div class="title-div text-center">
      <h2 class="mb-3">Mean slope and 95% CI</h2>
    </div>
    <transition :name="showGraph ? 'fade1' : 'fade2'" mode="out-in">
      <div
        v-if="requestError"
        class="overlay-div d-flex align-center justify-center"
      >
        <p class="mb-0 px-5 text-center">
          There seems to be a problem with our service. Please try again later.
          If the error persists, please notify us.
        </p>
      </div>
      <div
        v-else-if="fewPatients"
        class="overlay-div d-flex align-center justify-center"
      >
        <p class="mb-0 px-5 text-center">
          Cannot display fewer than 2 patients. Please expand your filtering
          criteria.
        </p>
      </div>
      <div
        v-else-if="fewModelPatients"
        class="overlay-div d-flex align-center justify-center"
      >
        <p class="mb-0 px-5 text-center">
          Cannot calculte mean slopes with so few patients. Please expand your
          filtering criteria.
        </p>
      </div>
      <line-chart
        v-else-if="showGraph"
        :chartData="chartData"
        :options="options"
        class="graph"
        id="MeanChart"
      ></line-chart>
      <div v-else class="loading-div d-flex align-center justify-center">
        <v-progress-circular
          indeterminate
          size="80"
          color="accent"
        ></v-progress-circular>
      </div>
    </transition>
  </div>
</template>

<style scoped>
@media (min-width: 960px) {
  .title-div {
    height: 52px;
  }
}

.loading-div {
  height: 400px;
}

.overlay-div {
  height: 400px;
  background-color: darkgray;
  border-radius: 15px;
}

.graph {
  max-height: 400px;
}

/* Transitions */
.fade1-enter-active,
.fade1-leave-active {
  opacity: 1;
  transition: all 0.25s ease;
}

.fade1-enter,
.fade1-leave-to,
.fade2-enter,
.fade2-leave-to {
  opacity: 0;
}

.fade2-enter-active,
.fade2-leave-active {
  opacity: 1;
}
</style>

<script>
import LineChart from "@/components/charts/LineChart";
import { mapState } from "vuex";
import { confInterval } from "@/helper_functions";

const prepareLine = function(slope, intercept, filled = false) {
  // Prepare the two points that will define the line
  // Point 1 will be on the y axis
  let point1 = {
    x: 0,
    y: intercept
  };

  // Point 2 will be on the x axis
  let point2 = {
    x: -(intercept / slope),
    y: 0
  };

  let slopeData = [point1, point2];

  let line = {
    fill: filled ? 1 : false,
    lineTension: 0,
    borderColor: filled ? fillColorStronger : lineColor,
    backgroundColor: filled ? fillColor : false,
    pointRadius: 0,
    pointHoverRadius: 0,
    data: slopeData,
    borderWidth: filled ? 0 : 2,
    pointHoverBorderWidth: 2
  };

  return line;
};

const fillColor = "rgba(0, 131, 143, 0.2)";
const fillColorStronger = "rgba(0, 131, 143, 0.5)";
const lineColor = "#EF5350";

export default {
  components: {
    LineChart
  },
  props: {
    patientData: {
      type: Array,
      default: () => []
    },
    fewPatients: {
      type: Boolean,
      default: false
    },
    highlightGroup: {
      type: Boolean,
      default: false
    },
    requestError: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    datasets: [],
    showGraph: false,
    fewModelPatients: false
  }),
  computed: {
    ...mapState(["axisMax", "axisMaxAuto"]),
    options() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        tooltips: {
          enabled: true
        },
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              type: "linear",
              position: "bottom",
              ticks: {
                max: this.axisMaxAuto ? undefined : this.axisMax
              },
              scaleLabel: {
                display: true,
                labelString: "Days"
              }
            }
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "MRC score / 20"
              },
              ticks: {
                min: 0,
                max: 20
              }
            }
          ]
        }
      };
    },
    chartData() {
      return {
        datasets: this.datasets
      };
    }
  },
  methods: {
    prepareDatasets() {
      let datasets = [];
      this.fewModelPatients = false;

      // Keep only the patients that had the model fitted
      let patients = this.patientData.filter(x => x.modelFitted);

      // If they are fewer than 2, exit the function
      if (patients.length < 2) {
        this.fewModelPatients = true;
        return;
      }

      // Create an array of the slopes
      let slopes = patients.map(x => x.slope);

      // Calculate the intercepts
      let intercepts = [];

      patients.forEach(function(patient) {
        let slope = patient.slope;
        let point = patient.assessments[0];
        let x = point.days;
        let y = point.fitted;
        let intercept = y - slope * x;
        intercepts.push(intercept);
      });

      // Calculte mean and CIs of slope and intercepts
      let slopeCIs = confInterval(slopes);
      let interceptsCIs = confInterval(intercepts);

      // Show error if any of the slopes is positive or
      // any of the intercepts is negative
      // If they are fewer than 2, exit the function
      let checks = slopeCIs.filter(x => x > 0);
      checks.concat(interceptsCIs.filter(x => x < 0));
      if (checks.length !== 0) {
        this.fewModelPatients = true;
        return;
      }

      // Final variables
      // We divide with 5 to calculate mrc20 equivalent from mrc100
      let slopeCI1 = slopeCIs[0] / 5;
      let slopeMean = slopeCIs[1] / 5;
      let slopeCI2 = slopeCIs[2] / 5;

      let interceptCI1 = interceptsCIs[0] / 5;
      let interceptMean = interceptsCIs[1] / 5;
      let interceptCI2 = interceptsCIs[2] / 5;

      datasets = [
        prepareLine(slopeMean, interceptMean),

        prepareLine(slopeCI1, interceptCI1, true),
        prepareLine(slopeCI2, interceptCI2, true)
      ];

      this.datasets = datasets;
    },
    async rerenderChart() {
      this.showGraph = false;
      this.prepareDatasets();
      await this.$nextTick();
      this.showGraph = true;
    }
  },
  watch: {
    patientData: {
      immediate: true,
      handler(value) {
        if (value.length > 0) {
          this.prepareDatasets();
          this.showGraph = true;
        } else {
          this.showGraph = false;
        }
      }
    },
    axisMaxAuto: function() {
      this.rerenderChart();
    },
    axisMax: function() {
      this.rerenderChart();
    }
  }
};
</script>
